import { onMounted, onUnmounted, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useRoute } from 'vue-router'
import { useCampaignService } from '@/services/campaign/useCampaignService'
import { useWhatsAppCoordinator } from '@/components/whatsapp/stores/useWhatsAppCoordinator'
import type { UserTookChatEvent } from '@/components/whatsapp/types/interfaces'
import { useAuthStore } from '@/stores/useAuthStore'
import { useWorkspaceStore } from '@/stores/useWorkspaceStore'
import { usePauseStatusStore } from '@/stores/usePauseStatusStore'

// Mounted once in the dashboard so activity stays current on every route.
export const useShiftActivitySync = () => {
  const route = useRoute()
  const authStore = useAuthStore()
  const workspace = useWorkspaceStore()
  const whatsAppCoordinator = useWhatsAppCoordinator()
  const { getShiftActivity } = useCampaignService()
  const { isPaused } = storeToRefs(usePauseStatusStore())
  const CALL_ENDED_EVENT = 'calloperator:call-ended'

  const loadShiftActivity = async () => {
    const userId = authStore.userId
    const alias = authStore.userAlias
    if (!userId || !alias) return

    const result = await getShiftActivity(userId, alias)
    if (authStore.userId !== userId || authStore.userAlias !== alias) return
    workspace.setShiftActivity(Array.isArray(result) ? result : [])
  }

  // Home is the Calls section. Calls received while it is open are already seen.
  watch(
    [() => route.path, () => workspace.missedCallsCount],
    ([path]) => {
      if (path === '/') workspace.acknowledgeMissedCalls()
    },
    { immediate: true, flush: 'sync' },
  )

  const handleCallEnded = () => {
    void loadShiftActivity().catch(() => {})
  }

  // Una tanda de chats no puede disparar una consulta por cada uno.
  const SHIFT_REFRESH_DEBOUNCE_MS = 1500

  // Eventos que significan "este chat pasó a ser mío", que es lo que mueve el
  // contador de chats atendidos:
  // - TRANSFER_ACCEPTED: en una transferencia el backend le abre fila en
  //   `chat_users` al agente que recibe, y este es el único evento que llega —
  //   no hay USER_TOOK_CHAT en ese camino. Lo reciben los dos implicados: para
  //   quien la entrega el número no cambia, pero refrescar de más no duele.
  // - NEW_CHAT_TEMPLATE: el chat que llega por respuesta a una plantilla, o el
  //   que uno mismo abre enviando una, también deja fila.
  const CHAT_ASSIGNED_EVENTS = ['TRANSFER_ACCEPTED', 'NEW_CHAT_TEMPLATE']

  let refreshTimer: number | null = null

  const scheduleShiftActivityRefresh = () => {
    if (refreshTimer !== null) return

    refreshTimer = window.setTimeout(() => {
      refreshTimer = null
      void loadShiftActivity().catch(() => {})
    }, SHIFT_REFRESH_DEBOUNCE_MS)
  }

  // USER_TOOK_CHAT va a todos los agentes de la campaña, no solo al que tomó el
  // chat: sin este filtro cada toma de cualquier compañero pediría el panel.
  const handleUserTookChat = (data: unknown) => {
    const tookChatEvent = data as UserTookChatEvent
    if (tookChatEvent?.user_id !== authStore.userId) return

    scheduleShiftActivityRefresh()
  }

  onMounted(() => {
    void loadShiftActivity().catch(() => {})
    window.addEventListener(CALL_ENDED_EVENT, handleCallEnded)

    whatsAppCoordinator.webSocketStore.on('USER_TOOK_CHAT', handleUserTookChat)
    CHAT_ASSIGNED_EVENTS.forEach((event) => {
      whatsAppCoordinator.webSocketStore.on(event, scheduleShiftActivityRefresh)
    })
  })

  onUnmounted(() => {
    window.removeEventListener(CALL_ENDED_EVENT, handleCallEnded)

    whatsAppCoordinator.webSocketStore.off('USER_TOOK_CHAT', handleUserTookChat)
    CHAT_ASSIGNED_EVENTS.forEach((event) => {
      whatsAppCoordinator.webSocketStore.off(event, scheduleShiftActivityRefresh)
    })

    if (refreshTimer !== null) window.clearTimeout(refreshTimer)
  })

  watch(isPaused, (currentIsPaused, previousIsPaused) => {
    if (previousIsPaused && !currentIsPaused) {
      void loadShiftActivity().catch(() => {})
    }
  })
}
